/**
 * The `product` section — RO-3-05 (task #44298).
 *
 * Scope (docs/rollover/inventory/pages.md § Product — 6 pages, of which three
 * live under `product/`):
 *
 * 1. `product/page.tsx` (39 LOC DELEGATOR) — ModePage with ProductDashboard in
 *    the dashboardSlot. The Mode Shell spine is already ported (RO-3-00c); this
 *    item only wires the product-specific Delivery ledger that used to be an
 *    in-process server component (`_dashboard.tsx` → `_dashboard-client.tsx`).
 *    That ledger is now `useApi(product.dashboard)`.
 *
 * 2. `product/[...segments]/page.tsx` (16 LOC DELEGATOR) — ModeSectionRoute.
 *    RO-3-14 registered `/product/$` as a PENDING placeholder; this file
 *    replaces it with the real body and removes it from PENDING_SEGMENT_PATHS.
 *
 * 3. `product/o/[uid]/page.tsx` — pure redirect, already PORTED by RO-3-14 in
 *    `dynamic-segments.tsx`. Not re-registered here.
 *
 * Properties pages live in `properties.tsx` (same section item, different URL
 * prefix). Live Next pages under `excellent/app/product/**` are UNTOUCHED (D6).
 */

import * as React from "react";
import { createRoute } from "@tanstack/react-router";
import {
  Band,
  Card,
  Section,
  Ledger,
  MetricStrip,
  ScopedTaskLifecycleList,
  Icon,
  formatDate,
  formatRelativeTime,
  type DataTableColumn,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { product } from "@excellent/ui/api/contracts/product";
import type {
  ProductDashIssue,
  ProductDashProject,
  ProductDashTask,
} from "@excellent/ui/api/contracts/product";
import { MODE_VIEWS, type ModeView } from "@/modes/modes-registry";
import { ModePage } from "../shared/modes/mode-page";
import {
  ModeSectionRoute,
  redirectAreaToPath,
} from "../shared/modes/mode-section-route";
import { useNavigate, useSearch } from "@excellent/ui/router";
import { rootRoute } from "./__root";

// ── Product dashboard (ported client of _dashboard-client.tsx) ──────────────

type ScopeKey = "shipped" | "review";

const PROJECT_COLUMNS: DataTableColumn<ProductDashProject>[] = [
  {
    key: "project",
    header: "Project",
    width: "w-6/12",
    render: (p) => (
      <span className="truncate font-medium text-foreground">{p.name}</span>
    ),
  },
  {
    key: "issues",
    header: "Issues",
    width: "w-4/12",
    render: (p) => (
      <span className="truncate text-muted-foreground">
        {p.open} open · {p.doing} doing · {p.review} review
      </span>
    ),
  },
  {
    key: "lastShip",
    header: "Last ship",
    width: "w-2/12",
    render: (p) => (
      <span className="truncate text-muted-foreground">
        {p.lastShip ? formatRelativeTime(p.lastShip) : "—"}
      </span>
    ),
  },
];

const ISSUE_COLUMNS: DataTableColumn<ProductDashIssue>[] = [
  {
    key: "issue",
    header: "Issue",
    width: "w-10/12",
    render: (t) => (
      <span className="truncate text-foreground">
        <span className="text-muted-foreground">#{t.id}</span> {t.title}
      </span>
    ),
  },
  {
    key: "due",
    header: "Due",
    width: "w-2/12",
    render: (t) => (
      <span className="truncate text-muted-foreground">
        {t.due ? formatDate(t.due) : "—"}
      </span>
    ),
  },
];

function ProductDashboard() {
  const { data, isLoading } = useApi(product.dashboard, {});
  const [scope, setScope] = React.useState<ScopeKey | null>(null);

  if (isLoading && !data) return null;
  if (!data) return null;

  const {
    shippedThisWeek,
    shippedPrevWeek,
    shippedDelta,
    inReview,
    shippedRows,
    reviewRows,
    projectRows,
    taskRows,
  } = data;

  const shippedUp = shippedDelta >= 0;
  const cells = [
    {
      key: "shipped" as const,
      label: "Shipped this week",
      value: String(shippedThisWeek),
      context: (
        <span className="inline-flex items-center gap-1">
          <Icon
            name={shippedUp ? "arrow-up" : "arrow-down"}
            size="xs"
            className={shippedUp ? "text-success" : "text-danger"}
          />
          vs {shippedPrevWeek} last week
        </span>
      ),
    },
    {
      key: "review" as const,
      label: "In review",
      value: String(inReview),
    },
  ];

  const strip = (
    <MetricStrip
      cells={cells}
      selected={scope ?? undefined}
      onSelect={(key) =>
        setScope((cur) => (cur === key ? null : (key as ScopeKey)))
      }
    />
  );

  const issueRows =
    scope === "shipped" ? shippedRows : scope === "review" ? reviewRows : null;

  // ScopedTaskLifecycleList expects ScopedTask; our DTO is a lean subset that
  // carries every field the WorkRow adapter reads.
  const tasks = taskRows as unknown as ProductDashTask[];

  return (
    <Band>
      <Section label="Delivery">
        {issueRows ? (
          <Ledger<ProductDashIssue>
            strip={strip}
            rows={issueRows}
            rowKey={(t) => t.id}
            rowHref={(t) => `/projects/${t.projectSlug}`}
            columns={ISSUE_COLUMNS}
            tableClassName="table-fixed"
            empty={
              scope === "shipped"
                ? "Nothing shipped in the last 7 days."
                : "Nothing in review."
            }
          />
        ) : (
          <Ledger<ProductDashProject>
            strip={strip}
            rows={projectRows}
            rowKey={(p) => p.slug}
            rowHref={(p) => `/projects/${p.slug}`}
            columns={PROJECT_COLUMNS}
            tableClassName="table-fixed"
            empty="No delivery activity yet."
          />
        )}
      </Section>

      <Section label="Issues">
        <Card className="gap-0 overflow-hidden py-0">
          <ScopedTaskLifecycleList
            tasks={tasks as never}
            empty="No open product issues."
            gutter
          />
        </Card>
      </Section>
    </Band>
  );
}

// ── /product ────────────────────────────────────────────────────────────────

function ProductModePage() {
  const navigate = useNavigate();
  // Seam useSearch (URLSearchParams), not productIndexRoute.useSearch():
  // RO-10-08 empty-splat handoff renders this from productSectionsRoute, and
  // a route-scoped useSearch throws when that route is not the match.
  const spParams = useSearch();
  const view = spParams.get("view") ?? undefined;
  const area = spParams.get("area") ?? undefined;
  const sub = spParams.get("sub") ?? undefined;
  const sp: Record<string, string | string[] | undefined> = {
    ...(view ? { view } : {}),
    ...(area ? { area } : {}),
    ...(sub ? { sub } : {}),
  };

  // Issue 3 flip: legacy `?area=` → canonical section path.
  React.useEffect(() => {
    redirectAreaToPath("product", sp, navigate);
  }, [area, sub, view, navigate]);

  const v = (MODE_VIEWS as readonly string[]).includes(view ?? "")
    ? (view as ModeView)
    : "dashboard";

  const dashboardSlot =
    v === "dashboard" && !area && !sub ? (
      <ProductDashboard />
    ) : undefined;

  return (
    <ModePage
      mode="product"
      view={v}
      area={area}
      sub={sub}
      searchParams={sp}
      dashboardSlot={dashboardSlot}
    />
  );
}

export const productIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/product",
  component: ProductModePage,
});

// ── /product/$ ──────────────────────────────────────────────────────────────

function ProductSectionsPage() {
  const params = productSectionsRoute.useParams();
  const search = productSectionsRoute.useSearch();
  // TanStack splat: `_splat` holds the unmatched tail; also accept `*` / segments.
  const splat =
    (params as { _splat?: string })._splat ??
    (params as { "*"?: string })["*"] ??
    "";
  // RO-10-08: empty splat steals bare `/product` from productIndexRoute —
  // hand off so the mode root still paints (h1 "Product").
  if (!splat) return <ProductModePage />;
  const segments = splat.split("/").filter(Boolean);
  const sp: Record<string, string | string[] | undefined> = {
    ...(search.view ? { view: search.view } : {}),
    ...(search.area ? { area: search.area } : {}),
    ...(search.sub ? { sub: search.sub } : {}),
  };
  return (
    <ModeSectionRoute mode="product" segments={segments} searchParams={sp} />
  );
}

export const productSectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/product/$",
  // #44601: empty splat outranks bare `/product` — reject so the index wins
  // the match (render hand-off above stays as belt-and-suspenders).
  params: {
    parse: (raw) => {
      if (!raw._splat) return false;
      return raw;
    },
  },
  component: ProductSectionsPage,
});
